/*
 * Brewer quiz wishlist page.
 *
 * Spec: gear-content/brewer-quiz/SAVE-SPEC.md
 *
 * Reads a list from the b2b-signup service and renders it. Prices come from
 * /products/{handle}.js at request time, the same as the quiz, so nothing here
 * is ever a snapshot: the service stores handles and positions and no money.
 *
 * The token comes from ?k= in the URL, or from localStorage if the visitor
 * saved on this device. No token means nothing to show, and the page says so
 * plainly rather than looking broken.
 */
(function () {
  'use strict';

  var STORE_KEY = 'stillwater:wishlist-token';

  function readConfig(root) {
    var el = root.querySelector('[data-wl-config]');
    try { return JSON.parse(el.textContent); } catch (e) { return {}; }
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function money(n) {
    var v = Number(n);
    var whole = Math.round(v * 100) % 100 === 0;
    return '$' + v.toLocaleString('en-CA', {
      minimumFractionDigits: whole ? 0 : 2,
      maximumFractionDigits: whole ? 0 : 2
    });
  }

  // The quiz's buttons for a machine we do not sell: one per country, shown by
  // gear-region.js. A country with no seller says so instead of going blank.
  var DISCLOSURE = 'Some links to other retailers earn us a commission. It does not change which machines we pick or what you pay.';

  function buyButtonsHtml(buy) {
    var html = '';
    ['ca', 'us'].forEach(function (region) {
      var b = buy[region];
      html += '<span class="wishlist__buy" data-gear-region-item="' + region + '">';
      if (b) {
        html += '<a class="button button--primary" href="' + esc(b.url) + '" target="_blank" rel="' +
          (b.paid ? 'sponsored ' : '') + 'nofollow noopener">' + esc(b.label) + '</a>';
      } else {
        html += '<span class="wishlist__nobuy">Not sold in ' + (region === 'us' ? 'the US' : 'Canada') + '</span>';
      }
      html += '</span>';
    });
    return html;
  }

  function disclosureHtml(buy) {
    var html = '';
    ['ca', 'us'].forEach(function (region) {
      var b = buy[region];
      if (!b || !b.paid) return;
      html += '<p class="caption wishlist__disclosure" data-gear-region-item="' + region + '">' +
        esc(DISCLOSURE + (b.disclosure ? ' ' + b.disclosure : '')) + '</p>';
    });
    return html;
  }

  function tokenFromUrl() {
    var m = window.location.search.match(/[?&]k=([^&]+)/);
    return m ? decodeURIComponent(m[1]) : null;
  }

  function Wishlist(root) {
    this.root = root;
    this.cfg = readConfig(root);
    this.stage = root.querySelector('[data-wl-stage]');
    this.token = tokenFromUrl();

    if (this.token) {
      try { localStorage.setItem(STORE_KEY, this.token); } catch (e) { /* private mode */ }
    } else {
      try { this.token = localStorage.getItem(STORE_KEY); } catch (e) { this.token = null; }
    }
  }

  Wishlist.prototype.api = function (path, options) {
    return fetch(this.cfg.serviceUrl.replace(/\/$/, '') + path, options);
  };

  // Signed in, on a device that never saved anything: no ?k= and no
  // localStorage. Look the list up by the customer id, which is the same signal
  // page.coffee-ratings.liquid and b2b-member-banner.liquid already use because
  // the `customer` Liquid object is unreliable under New Customer Accounts.
  Wishlist.prototype.customerId = function () {
    var m = window.ShopifyAnalytics && window.ShopifyAnalytics.meta;
    return (m && m.page && m.page.customerId) ? String(m.page.customerId) : null;
  };

  Wishlist.prototype.start = function () {
    var self = this;

    if (!this.token) {
      var cid = this.customerId();
      if (!cid) return this.renderEmpty();

      this.stage.innerHTML = '<p class="wishlist__status center">Loading your wishlist…</p>';
      return this.api('/wishlist/by-customer/' + encodeURIComponent(cid))
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (data) {
          if (!data || !data.items || !data.items.length) return self.renderEmpty();
          self.token = data.token;
          try { localStorage.setItem(STORE_KEY, self.token); } catch (e) { /* private mode */ }
          self.items = data.items;
          return self.hydrate().then(function () { self.render(); });
        })
        .catch(function () { self.renderEmpty(); });
    }

    this.stage.innerHTML = '<p class="wishlist__status center">Loading your wishlist…</p>';
    this.api('/wishlist/' + encodeURIComponent(this.token))
      .then(function (r) {
        if (r.status === 404) return null;
        if (!r.ok) throw new Error('service ' + r.status);
        return r.json();
      })
      .then(function (data) {
        if (!data) return self.renderEmpty('That link has expired or was never a wishlist.');
        self.items = (data.items || []);
        if (!self.items.length) return self.renderEmpty();
        return self.hydrate().then(function () { self.render(); });
      })
      .catch(function () {
        self.stage.innerHTML =
          '<p class="wishlist__status center">We could not load your wishlist just now. ' +
          'Please refresh, or <a href="' + esc(self.cfg.quizUrl) + '">take the quiz again</a>.</p>';
      });
  };

  // The quiz's machine list, fetched once and only when the list holds a
  // machine we do not sell.
  Wishlist.prototype.matrix = function () {
    if (!this.matrixPromise) {
      this.matrixPromise = this.cfg.matrixUrl
        ? fetch(this.cfg.matrixUrl).then(function (r) { return r.ok ? r.json() : null; })
            .catch(function () { return null; })
        : Promise.resolve(null);
    }
    return this.matrixPromise;
  };

  // A machine we do not sell has no product page. The quiz can still recommend
  // it (Dean, 2026-10-10), and its name, photo, Canadian price and buy buttons
  // ride in the quiz's matrix, from the pick library entry a gear article
  // renders. One that has left the matrix is dropped like a deleted product.
  Wishlist.prototype.fromMatrix = function (item) {
    return this.matrix().then(function (data) {
      var entry = ((data && data.products) || []).filter(function (p) {
        return p.sold === 0 && p.handle === item.handle;
      })[0];
      if (!entry) { item.gone = true; return; }
      item.title = entry.title;
      item.price = entry.price;
      item.available = true;
      item.image = entry.image || null;
      item.buy = entry.buy || {};
      item.external = true;
    });
  };

  // Prices, titles and images from the products themselves. A machine that no
  // longer resolves is dropped rather than shown at a price we made up.
  Wishlist.prototype.hydrate = function () {
    var self = this;
    return Promise.all(this.items.map(function (item) {
      return fetch('/products/' + item.handle + '.js', { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (p) {
          if (!p) return self.fromMatrix(item);
          var variants = p.variants || [];
          var inStock = variants.filter(function (v) { return v.available; });
          var pool = inStock.length ? inStock : variants;
          var v = pool.sort(function (a, b) { return a.price - b.price; })[0];
          if (!v) { item.gone = true; return; }
          item.title = p.title;
          item.price = v.price / 100;
          item.available = !!v.available;
          item.image = (v.featured_image && v.featured_image.src) || p.featured_image || null;
          item.url = '/products/' + p.handle;
          item.productId = p.id;
        })
        .catch(function () { item.gone = true; });
    }));
  };

  Wishlist.prototype.renderEmpty = function (message) {
    this.stage.innerHTML =
      '<div class="wishlist__empty center">' +
      '<p class="wishlist__status">' + esc(message || this.cfg.emptyText) + '</p>' +
      '<a class="button button--primary" href="' + esc(this.cfg.quizUrl) + '">' +
      esc(this.cfg.quizCta) + '</a></div>';
  };

  Wishlist.prototype.render = function () {
    var self = this;
    var live = this.items.filter(function (i) { return !i.gone; });
    if (!live.length) return this.renderEmpty();

    // Take the quiz twice and two machines can each have been ranked first, in
    // their own run. Both are true, but two cards saying "we ranked this first"
    // reads as a bug. Only the most recent one carries the flag.
    var newestFirstPick = null;
    live.forEach(function (item) {
      if (item.position !== 1) return;
      if (!newestFirstPick || String(item.added_at) > String(newestFirstPick.added_at)) {
        newestFirstPick = item;
      }
    });

    var html = '<ul class="wishlist__list">';
    live.forEach(function (item) {
      html += '<li class="wishlist__item color-' + esc(self.cfg.cardScheme) + ' gradient">';
      if (item.image && item.external) {
        html += '<span class="wishlist__thumb">' +
          '<img src="' + esc(item.image) + '" alt="' + esc(item.title) + '" loading="lazy" width="120" height="120"></span>';
      } else if (item.image) {
        html += '<a class="wishlist__thumb" href="' + esc(item.url) + '">' +
          '<img src="' + esc(item.image) + '" alt="' + esc(item.title) + '" loading="lazy" width="120" height="120"></a>';
      }
      html += '<div class="wishlist__body">';
      if (item === newestFirstPick) {
        html += '<p class="caption-with-letter-spacing wishlist__flag">We ranked this first for you</p>';
      }
      html += '<h2 class="h4 wishlist__title">' + (item.external
        ? esc(item.title)
        : '<a href="' + esc(item.url) + '">' + esc(item.title) + '</a>') + '</h2>';
      // A machine we do not sell has its price on the button, as in the quiz.
      if (!item.external) {
        html += '<p class="wishlist__price">' + money(item.price) +
          (item.available ? '' : ' <span class="wishlist__oos">Out of stock</span>') + '</p>';
      }
      html += '</div>';
      html += '<div class="wishlist__actions">';
      if (item.external) {
        html += buyButtonsHtml(item.buy);
      } else {
        html += '<a class="button button--primary" href="' + esc(item.url) + '">View</a>';
      }
      html += '<button type="button" class="link wishlist__remove" data-handle="' +
        esc(item.handle) + '">Remove</button>';
      html += '</div>';
      if (item.external) html += disclosureHtml(item.buy);
      html += '</li>';
    });
    html += '</ul>';
    html += '<div data-wl-recs></div>';
    html += '<div class="wishlist__footer center">' +
      '<a class="button" href="' + esc(this.cfg.quizUrl) + '">' + esc(this.cfg.quizCta) + '</a></div>';

    this.stage.innerHTML = html;

    Array.prototype.forEach.call(this.stage.querySelectorAll('.wishlist__remove'), function (btn) {
      btn.addEventListener('click', function () { self.remove(btn.dataset.handle, btn); });
    });

    this.renderRecommendations(live);
  };

  // Shopify's own related-products endpoint, seeded from the machine they kept
  // most recently. Using the platform's recommendations rather than a rule we
  // invented, and anything already on the list is filtered out.
  Wishlist.prototype.renderRecommendations = function (live) {
    var self = this;
    var slot = this.stage.querySelector('[data-wl-recs]');
    if (!slot || !this.cfg.recsHeading) return;

    var seed = live.filter(function (i) { return i.productId; }).pop();
    if (!seed || !seed.productId) return;
    var have = {};
    live.forEach(function (i) { have[i.handle] = true; });

    fetch('/recommendations/products.json?product_id=' + seed.productId +
          '&limit=8&intent=related', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        if (!data) return;
        // Drop anything that is the same machine in a different finish. Shopify
        // recommended "Lelit Bianca" (the colour variant, $4,750) directly under
        // the saved "Lelit Bianca - Stainless Steel" at $4,595, which is not a
        // suggestion, it is the same machine costing more. Handles nest, so a
        // prefix match either way catches it.
        var sameMachine = function (handle) {
          return Object.keys(have).some(function (saved) {
            return handle === saved ||
              handle.indexOf(saved + '-') === 0 ||
              saved.indexOf(handle + '-') === 0;
          });
        };
        var picks = (data.products || [])
          .filter(function (p) { return !sameMachine(p.handle); })
          .slice(0, 4);
        if (!picks.length) return;

        var out = '<div class="wishlist__recs">';
        out += '<h2 class="h4 wishlist__recs-title">' + esc(self.cfg.recsHeading) + '</h2>';
        out += '<ul class="wishlist__recs-list">';
        picks.forEach(function (p) {
          var v = (p.variants || [])[0] || {};
          out += '<li class="wishlist__rec">';
          out += '<a href="/products/' + esc(p.handle) + '">';
          if (p.featured_image) {
            out += '<img src="' + esc(p.featured_image) + '" alt="' + esc(p.title) +
              '" loading="lazy" width="160" height="160">';
          }
          out += '<span class="wishlist__rec-title">' + esc(p.title) + '</span>';
          out += '<span class="wishlist__rec-price">' + money((v.price || 0) / 100) + '</span>';
          out += '</a></li>';
        });
        out += '</ul></div>';
        slot.innerHTML = out;
      })
      .catch(function () { /* recommendations are a bonus, never an error */ });
  };

  Wishlist.prototype.remove = function (handle, btn) {
    var self = this;
    btn.disabled = true;
    btn.textContent = 'Removing…';
    this.api('/wishlist/remove', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ token: this.token, handle: handle })
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        if (!data) throw new Error('remove failed');
        // Keep what we already fetched rather than re-fetching every product.
        var kept = {};
        (data.items || []).forEach(function (i) { kept[i.handle] = i.position; });
        self.items = self.items.filter(function (i) { return kept.hasOwnProperty(i.handle); });
        self.render();
      })
      .catch(function () {
        btn.disabled = false;
        btn.textContent = 'Remove';
        // Say it failed rather than leaving a button that looks like it worked.
        btn.insertAdjacentHTML('afterend',
          '<span class="wishlist__error">Could not remove that. Try again.</span>');
      });
  };

  function init() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-wl-root]'), function (root) {
      new Wishlist(root).start();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
